import React, { useState } from 'react';
import { useBrand } from '../../../app/config/brand';
import { deploymentConfig } from '../../../config/deployment';
import { Button } from '../../../shared/components/ui/Button';
import { Logo } from '../../../shared/components/ui/Logo';

interface AdminRegistrationPageProps {
  onNavigate: (page: string) => void;
  onSignup: (options?: { login_hint: string; screen_hint: string }) => void;
}

const AdminRegistrationPage: React.FC<AdminRegistrationPageProps> = ({ onNavigate, onSignup }) => {
  const brand = useBrand();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Staff email domains are deployment identity — config/deployment.ts owns
  // them (lower-cased, never empty) and their transitional default.
  const allowedDomains = deploymentConfig.staffEmailDomains;
  const allowedDomainsLabel = allowedDomains.map((allowed) => `@${allowed}`).join(', ');

  const validateEmail = (emailValue: string) => {
    if (!emailValue) return 'Email is required';
    const domain = emailValue.split('@')[1]?.toLowerCase();
    if (!allowedDomains.includes(domain)) {
      return `Only ${allowedDomainsLabel} email addresses are allowed for admin registration`;
    }
    return '';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }

    if (!firstName || !lastName) {
      setError('First name and last name are required');
      return;
    }

    setIsSubmitting(true);
    onSignup({
      login_hint: email,
      screen_hint: 'signup',
    });
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: brand.colors.bgLight }}>
      {/* Left side - branding */}
      <div className="flex-1 p-12 flex flex-col justify-center">
        <Logo className="mb-12" />
        <h1 className="text-5xl mb-6" style={{ fontFamily: brand.typography.fontFamily.heading, color: brand.colors.secondary }}>
          Admin Portal<br />Registration
        </h1>
        <p className="mb-4" style={{ color: brand.colors.textLight }}>
          This registration is for {brand.company.name} staff only.
        </p>
        <p className="mb-4" style={{ color: brand.colors.textLight }}>
          Only <span className="font-semibold">{allowedDomainsLabel}</span> email addresses are permitted.
        </p>
        <p style={{ color: brand.colors.textLight }}>
          Are you a customer?{' '}
          <button onClick={() => onNavigate('login')} style={{ color: brand.colors.link }} className="font-medium bg-transparent border-none cursor-pointer">
            Go to Customer Portal
          </button>
        </p>
      </div>

      {/* Right side - registration form */}
      <div className="w-[480px] bg-white p-12 flex flex-col justify-center shadow-xl">
        <h2 className="text-2xl font-semibold mb-2" style={{ color: brand.colors.secondary }}>
          Register as Admin
        </h2>
        <p className="mb-6 text-sm" style={{ color: brand.colors.textLight }}>
          Enter your company email to get started
        </p>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="admin-reg-firstName" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>First Name</label>
              <input id="admin-reg-firstName" type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2" style={{ borderColor: brand.colors.border }} placeholder="John" required />
            </div>
            <div>
              <label htmlFor="admin-reg-lastName" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Last Name</label>
              <input id="admin-reg-lastName" type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} className="w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2" style={{ borderColor: brand.colors.border }} placeholder="Doe" required />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="admin-reg-email" className="block text-sm font-medium mb-1" style={{ color: brand.colors.text }}>Work Email</label>
            <input id="admin-reg-email" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} className="w-full px-4 py-3 border rounded-lg focus:outline-hidden focus:ring-2" style={{ borderColor: error ? brand.colors.error : brand.colors.border }} placeholder={`you@${allowedDomains[0]}`} required />
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm" style={{ backgroundColor: brand.colors.error + '15', color: brand.colors.error }}>
              {error}
            </div>
          )}

          <Button type="submit" className="w-full mb-4" size="lg" disabled={isSubmitting}>
            {isSubmitting ? 'Processing...' : 'Create Account'}
          </Button>
        </form>

        <p className="text-center text-sm" style={{ color: brand.colors.textLight }}>
          Already have an account?{' '}
          <button onClick={() => onNavigate('login')} style={{ color: brand.colors.link }} className="font-medium bg-transparent border-none cursor-pointer">Log in</button>
        </p>
      </div>
    </div>
  );
};

export default AdminRegistrationPage;
